import React from "react";
import { Link } from "react-router-dom";
import { Flower2, Tractor, PartyPopper, PawPrint } from "lucide-react";

const TILES = [
  { Icon: Flower2, title: "Private Pickings", blurb: "Your own hosted hour or two among the dahlias , pick, wander, meet the farm.", href: "#book", label: "Book a picking", testid: "visit-tile-pickings" },
  { Icon: Tractor, title: "The Farm Experience", blurb: "Every visit is a whole-farm visit: growing areas, gardens and behind the scenes.", href: "#farm", label: "What's included", testid: "visit-tile-farm" },
  { Icon: PartyPopper, title: "Heritage Open Days", blurb: "Three seasonal parties , opening, mid-summer picking and seed saving.", href: "#open-days", label: "See the events", testid: "visit-tile-open-days" },
];

const ANIMALS = [
  { name: "Burger & Blue", role: "Our Dexter cattle", img: "/visit/dexters.jpg", blurb: "Small, glossy and deeply nosy. They keep the paddocks tidy and will absolutely judge your picking technique.", testid: "visit-animal-dexters" },
  { name: "Messy", role: "The lamb", img: "/visit/messy.jpg", blurb: "Named honestly. Messy believes every visitor has brought a snack and every bucket is for her.", testid: "visit-animal-messy" },
  { name: "The Spaniels", role: "Our four Springer Spaniels", img: "/visit/spaniels.jpg", blurb: "Four sets of ears, one shared brain cell, endless enthusiasm. Official welcoming committee.", testid: "visit-animal-spaniels" },
];

export function VisitTiles() {
  return (
    <section id="visit" data-testid="visit-heritage" className="bg-[var(--dahlias-cream)] border-b-[3px] border-[var(--berry)]/30">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-12">
        <div className="font-label tracking-[0.4em] text-[10px] text-[var(--berry)] text-center">✦ VISIT HERITAGE ✦</div>
        <h2 className="font-display text-3xl md:text-4xl text-[var(--dahlias-ink)] text-center mt-2">Three ways to spend a day with us</h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {TILES.map(({ Icon, title, blurb, href, label, testid }) => (
            <a key={title} href={href} data-testid={testid}
              className="group rounded-[28px] border-[3px] border-[var(--berry)]/25 bg-white p-6 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-transform">
              <Icon className="w-8 h-8 text-[var(--berry)]" />
              <h3 className="font-display text-2xl text-[var(--dahlias-ink)] mt-3">{title}</h3>
              <p className="mt-2 text-sm text-[var(--dahlias-ink)]/75 leading-relaxed">{blurb}</p>
              <span className="inline-block mt-4 font-label text-[10px] tracking-[0.3em] uppercase text-[var(--berry)] group-hover:underline">{label} →</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FarmExperience() {
  return (
    <section id="farm" data-testid="visit-farm-experience" className="bg-white border-b-[3px] border-[var(--berry)]/30">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 grid gap-10 md:grid-cols-2 items-start">
        <div>
          <div className="font-label tracking-[0.4em] text-[10px] text-[var(--berry)]">✦ THE FARM EXPERIENCE ✦</div>
          <h2 className="font-display text-3xl md:text-4xl text-[var(--dahlias-ink)] mt-2">It's never just the flowers</h2>
          <p className="mt-4 text-[var(--dahlias-ink)]/80 leading-relaxed">
            A Private Picking is a visit to the whole of Heritage. You'll be hosted by us, walk the growing areas and gardens,
            peek behind the scenes at how a small dahlia farm actually runs, and pick with a bucket in one hand and a cuppa in the other.
          </p>
          <ul className="mt-5 space-y-2 text-sm text-[var(--dahlias-ink)]/85" data-testid="visit-farm-list">
            {["A hosted farm tour with the people who grow the flowers", "The dahlia gardens and growing beds at their best", "Behind the scenes , tuber sheds, propagation and packing", "Time with the animals (see below, terms and conditions apply, they're animals)", "Your picking hour, blooms wrapped to take home"].map((t) => (
              <li key={t} className="flex gap-2"><span className="text-[var(--berry)]">✿</span>{t}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-[32px] overflow-hidden border-[3px] border-[var(--berry)]/25 shadow-md">
          <img src="/visit/spaniels.jpg" alt="The Heritage spaniels between rows of dahlias" className="w-full h-full object-cover aspect-[4/3]" loading="lazy" />
        </div>
      </div>
    </section>
  );
}

export function WhoYouMightMeet() {
  return (
    <section id="animals" data-testid="visit-animals" className="bg-[var(--dahlias-cream)] border-b-[3px] border-[var(--berry)]/30">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-14">
        <div className="text-center">
          <div className="font-label tracking-[0.4em] text-[10px] text-[var(--berry)]">✦ WHO YOU MIGHT MEET ✦</div>
          <h2 className="font-display text-3xl md:text-4xl text-[var(--dahlias-ink)] mt-2 inline-flex items-center gap-3"><PawPrint className="w-7 h-7 text-[var(--berry)]" /> The Heritage locals</h2>
        </div>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {ANIMALS.map((a) => (
            <article key={a.name} data-testid={a.testid} className="rounded-[28px] overflow-hidden bg-white border-[3px] border-[var(--berry)]/25 shadow-sm hover:-translate-y-1 transition-transform">
              <img src={a.img} alt={`${a.name} , ${a.role}`} className="w-full aspect-square object-cover" loading="lazy" />
              <div className="p-5">
                <div className="font-label text-[10px] tracking-[0.3em] uppercase text-[var(--berry)]">{a.role}</div>
                <h3 className="font-display text-2xl text-[var(--dahlias-ink)] mt-1">{a.name}</h3>
                <p className="mt-2 text-sm text-[var(--dahlias-ink)]/75 leading-relaxed">{a.blurb}</p>
              </div>
            </article>
          ))}
        </div>
        <p className="mt-6 text-center text-xs italic text-[var(--dahlias-ink)]/60" data-testid="visit-animal-disclaimer">
          Animals are animals , appearances, cuddles and good behaviour can't always be guaranteed. Messy in particular makes no promises.
        </p>
      </div>
    </section>
  );
}
